import { CalendarClock, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '~/components/ui/card';
import { formatCurrency, formatDate, calculateCardFee } from '~/utils/paymentUtils';

interface PaymentInfoCardProps {
  paymentMethod: string;
  tuitionInvoices: any[];
  firstFuturePaymentDate: string;
}

/**
 * PaymentInfoCard - Display automatic payment schedule and fees
 */
export function PaymentInfoCard({ paymentMethod, tuitionInvoices, firstFuturePaymentDate }: PaymentInfoCardProps) {
  const dueLaterTotal = tuitionInvoices.reduce((sum, inv) => sum + inv.amount, 0);
  const totalFutureCardFees = tuitionInvoices.reduce(
    (sum, inv) => sum + calculateCardFee(inv.amount),
    0
  );
  const averageMonthlyPayment = tuitionInvoices.length > 0
    ? Math.round(dueLaterTotal / tuitionInvoices.length)
    : 0;

  return (
    <Card className="border-primary">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5" />
          Automatic Payments
        </CardTitle>
        <CardDescription>
          Your future tuition payments will be charged automatically
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-start space-x-3 p-4 border rounded-lg border-primary bg-primary/5">
          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-muted-foreground">
              Your {paymentMethod === 'ach' ? 'bank account' : 'card'} will be automatically charged on each due date.
              No need to remember payment deadlines.
            </p>
            <div className="mt-3 text-sm bg-slate-50 p-3 rounded">
              <p className="font-medium">Starting {formatDate(firstFuturePaymentDate)}:</p>
              {paymentMethod === 'ach' ? (
                <p className="text-muted-foreground mt-1">
                  {tuitionInvoices.length} payment{tuitionInvoices.length !== 1 ? 's' : ''} totaling {formatCurrency(dueLaterTotal)}
                </p>
              ) : (
                <div className="mt-2 space-y-1">
                  <p className="text-muted-foreground">
                    {tuitionInvoices.length} payment{tuitionInvoices.length !== 1 ? 's' : ''} totaling {formatCurrency(dueLaterTotal)}
                  </p>
                  <div className="pt-2 border-t border-slate-200 space-y-1">
                    <div className="flex justify-between text-muted-foreground">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(dueLaterTotal)}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Processing fees ({tuitionInvoices.length} × (2.9% × {formatCurrency(averageMonthlyPayment)} + $0.30)):</span>
                      <span>{formatCurrency(totalFutureCardFees)}</span>
                    </div>
                    <div className="flex justify-between font-medium pt-1">
                      <span>Total with fees:</span>
                      <span>{formatCurrency(dueLaterTotal + totalFutureCardFees)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default PaymentInfoCard;
